import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, Truck, Mail, ArrowRight, Loader2, Home, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderConfirmation = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    if (orderId) {
      fetchOrder();
    } else {
      toast.error('Order not found');
      navigate('/shop');
    }
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050605] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#c8b273] animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#050605] flex flex-col items-center justify-center p-4">
        <Package className="w-16 h-16 text-[#4a504a] mb-4" />
        <h1 className="text-xl text-white mb-2">Order Not Found</h1>
        <p className="text-[#8a948a] mb-6">We couldn't find the order you're looking for.</p>
        <Button onClick={() => navigate('/shop')} className="btn-primary">
          Continue Shopping
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050605] py-12 px-4" data-testid="order-confirmation-page">
      <div className="max-w-3xl mx-auto">
        {/* Success Animation */}
        <motion.div 
          className="text-center mb-8"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.5 }}
        >
          <div className="w-24 h-24 bg-green-500/20 rounded-full mx-auto flex items-center justify-center mb-6">
            <CheckCircle className="w-16 h-16 text-green-500" />
          </div>
          <h1 className="font-['Barlow_Condensed'] text-4xl font-bold uppercase tracking-wider text-white mb-2">
            Order Confirmed!
          </h1>
          <p className="text-[#8a948a] text-lg">
            Thank you for your order. We've received your payment.
          </p>
        </motion.div>

        {/* Order Details Card */}
        <motion.div 
          className="glass-card p-6 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
            <div>
              <p className="text-[#6b7d6b] text-sm">Order Number</p>
              <p className="text-[#c8b273] font-mono text-lg font-bold">{order.order_id}</p>
            </div>
            <div className="text-right">
              <p className="text-[#6b7d6b] text-sm">Total Paid</p>
              <p className="text-white text-2xl font-bold">£{order.total_amount?.toFixed(2)}</p>
            </div>
          </div>

          {/* Status Timeline */}
          <div className="mb-6">
            <h3 className="text-white font-semibold mb-4">Order Status</h3>
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <span className="text-green-500 text-xs mt-2">Paid</span>
              </div>
              <div className="flex-1 h-1 bg-[#c8b273]"></div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-[#c8b273] rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-black" />
                </div>
                <span className="text-[#c8b273] text-xs mt-2">Processing</span>
              </div>
              <div className="flex-1 h-1 bg-[#2d352d]"></div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-[#2d352d] rounded-full flex items-center justify-center">
                  <Truck className="w-5 h-5 text-[#6b7d6b]" />
                </div>
                <span className="text-[#6b7d6b] text-xs mt-2">Shipped</span>
              </div>
            </div>
          </div>

          {/* Items Ordered */}
          <div className="mb-6">
            <h3 className="text-white font-semibold mb-4">Items Ordered</h3>
            <div className="space-y-3">
              {order.items?.map((item, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="text-white">{item.product_name}</p>
                    <p className="text-[#6b7d6b] text-sm">SKU: {item.sku} | Qty: {item.quantity}</p>
                  </div>
                  <p className="text-[#c8b273]">£{(item.unit_price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Delivery Address */}
          <div>
            <h3 className="text-white font-semibold mb-2">Delivery Address</h3>
            <p className="text-[#8a948a] whitespace-pre-line">{order.customer?.shipping_address}</p>
          </div>
        </motion.div>

        {/* What's Next */}
        <motion.div 
          className="glass-card p-6 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h3 className="text-white font-semibold mb-4">What Happens Next?</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-[#c8b273]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-[#c8b273] font-bold">1</span>
              </div>
              <div>
                <p className="text-white">Order sent to suppliers</p>
                <p className="text-[#6b7d6b] text-sm">Your order details have been sent to our suppliers for fulfillment.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-[#c8b273]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-[#c8b273] font-bold">2</span>
              </div>
              <div>
                <p className="text-white">Dispatch & Tracking</p>
                <p className="text-[#6b7d6b] text-sm">Once dispatched, you'll receive tracking information via email.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-[#c8b273]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-[#c8b273] font-bold">3</span>
              </div>
              <div>
                <p className="text-white">Delivery</p>
                <p className="text-[#6b7d6b] text-sm">Expect delivery within 3-5 working days.</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div 
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Button 
            onClick={() => navigate('/my-orders')}
            className="btn-primary"
          >
            <Package className="w-4 h-4 mr-2" />
            Track Order
          </Button>
          <Button 
            onClick={() => navigate('/shop')}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            Continue Shopping
          </Button>
          <Button 
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-[#8a948a] hover:text-white"
          >
            <Home className="w-4 h-4 mr-2" />
            Go to Dashboard
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
